import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildPublicProspectMapEmbedUrl, formatPublicProspectAddress } from './publicProspectMapUrl';
import {
  PUBLIC_PROSPECT_MAP_LOAD_TIMEOUT_MS,
  createPublicProspectMapLoadSession,
  ownsPublicProspectMapIframeEvent,
  reducePublicProspectMapLoadSession,
  shouldResetMapSessionForEmbedUrlChange,
} from './publicProspectMapLoad.js';

function MapShell({ children, title }) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-[#004747]/15 bg-[#eef6f7] shadow-sm"
      aria-label={title}
    >
      {children}
    </div>
  );
}

function MapStatusOverlay({
  address,
  title,
  fallbackLabel,
  retryLabel,
  onRetry,
  mode,
}) {
  const showRetry = mode === 'timed_out' || mode === 'errored';
  return (
    <div
      className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-3 bg-[#eef6f7] p-4 text-center text-[#1f4a52]"
      role="status"
      aria-live="polite"
    >
      {mode === 'loading' ? (
        <p className="prospect-ui text-sm">{title}</p>
      ) : (
        <>
          <div>
            <p className="prospect-ui font-semibold">{address || fallbackLabel}</p>
            {address ? <p className="prospect-ui mt-1 text-sm">{fallbackLabel}</p> : null}
          </div>
          {showRetry && onRetry ? (
            <button
              type="button"
              className="prospect-ui rounded-xl border border-[#004747] px-4 py-2 text-[1.0625rem] font-semibold text-[#004747] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
              onClick={onRetry}
            >
              {retryLabel}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

function MapUnavailableSurface({ address, fallbackLabel }) {
  return (
    <div
      className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 p-4 text-center text-[#1f4a52] lg:min-h-[320px]"
      role="status"
      aria-live="polite"
    >
      <div>
        <p className="prospect-ui font-semibold">{address || fallbackLabel}</p>
        {address ? <p className="prospect-ui mt-1 text-sm">{fallbackLabel}</p> : null}
      </div>
    </div>
  );
}

export default function PropertyMap({ property, title, fallbackLabel, retryLabel }) {
  const address = useMemo(() => formatPublicProspectAddress(property), [property]);
  const embedUrl = useMemo(() => buildPublicProspectMapEmbedUrl(property), [property]);
  const [session, setSession] = useState(() => createPublicProspectMapLoadSession(0));
  const timerRef = useRef(null);
  const previousEmbedUrlRef = useRef(undefined);

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    const previous = previousEmbedUrlRef.current;
    previousEmbedUrlRef.current = embedUrl;
    if (!shouldResetMapSessionForEmbedUrlChange(previous, embedUrl)) return;
    clearTimer();
    setSession((current) => reducePublicProspectMapLoadSession(current, { type: 'property_change' }));
  }, [embedUrl]);

  useEffect(() => {
    clearTimer();
    if (!embedUrl || session.status !== 'loading') return undefined;
    const sessionId = session.sessionId;
    timerRef.current = setTimeout(() => {
      setSession((current) =>
        reducePublicProspectMapLoadSession(current, { type: 'timeout', sessionId }),
      );
    }, PUBLIC_PROSPECT_MAP_LOAD_TIMEOUT_MS);
    return () => clearTimer();
  }, [embedUrl, session.sessionId, session.status]);

  useEffect(() => () => clearTimer(), []);

  const retry = () => {
    clearTimer();
    setSession((current) => reducePublicProspectMapLoadSession(current, { type: 'retry' }));
  };

  if (!embedUrl) {
    return (
      <MapShell title={title}>
        <MapUnavailableSurface address={address} fallbackLabel={fallbackLabel} />
      </MapShell>
    );
  }

  const showOverlay =
    session.status === 'loading' || session.status === 'timed_out' || session.status === 'errored';
  // Capture ownership for this rendered iframe instance — never read a mutable "current" ref in handlers.
  const iframeSessionId = session.sessionId;

  return (
    <MapShell title={title}>
      <div className="relative aspect-[4/3] w-full bg-[#eef6f7] lg:min-h-[320px]">
        <iframe
          key={session.remountKey}
          title={title}
          src={embedUrl}
          width="600"
          height="450"
          className="relative z-[1] block aspect-[4/3] h-full w-full border-0 lg:min-h-[320px]"
          loading="eager"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          onLoad={() => {
            // onLoad means the iframe document started; it does not prove map tiles rendered.
            setSession((current) => {
              if (!ownsPublicProspectMapIframeEvent(current.sessionId, iframeSessionId)) {
                return current;
              }
              clearTimer();
              return reducePublicProspectMapLoadSession(current, {
                type: 'iframe_load',
                sessionId: iframeSessionId,
              });
            });
          }}
          onError={() => {
            // Cross-origin iframe errors are not reliably observable; timeout remains the primary path.
            setSession((current) => {
              if (!ownsPublicProspectMapIframeEvent(current.sessionId, iframeSessionId)) {
                return current;
              }
              clearTimer();
              return reducePublicProspectMapLoadSession(current, {
                type: 'iframe_error',
                sessionId: iframeSessionId,
              });
            });
          }}
        />
        {showOverlay ? (
          <MapStatusOverlay
            mode={session.status}
            address={address}
            title={title}
            fallbackLabel={fallbackLabel}
            retryLabel={retryLabel}
            onRetry={retry}
          />
        ) : null}
      </div>
    </MapShell>
  );
}
